// Video Decoder Service - H.264 视频解码和渲染
// 支持WebCodecs API和降级方案

import { log } from './websocket'

// =============================
// 类型定义
// =============================

export interface VideoConfig {
  codec?: string
  width?: number
  height?: number
  enableHardwareAcceleration?: boolean
}

export interface VideoFrame {
  data: ArrayBuffer
  timestamp: number
  seq?: number
}

interface VideoDecoderState {
  decoder: VideoDecoder | null
  isInitialized: boolean
  frameQueue: VideoFrame[]
  targetElement: HTMLVideoElement | HTMLCanvasElement | null
  canvasContext: CanvasRenderingContext2D | null
  config: VideoConfig
}

// =============================
// 状态变量
// =============================

const state: VideoDecoderState = {
  decoder: null,
  isInitialized: false,
  frameQueue: [],
  targetElement: null,
  canvasContext: null,
  config: {
    codec: 'h264',
    width: 640,
    height: 480,
    enableHardwareAcceleration: true
  }
}

// 帧计数器
let frameCount = 0
let lastFrameTime = 0

// 视频播放状态回调
type VideoPlayingCallback = (isPlaying: boolean, fps: number) => void
let videoPlayingCallback: VideoPlayingCallback | null = null

// =============================
// 初始化
// =============================

/**
 * 检查浏览器是否支持WebCodecs API
 */
export function isVideoDecoderSupported(): boolean {
  if (typeof window === 'undefined') {
    return false
  }
  
  // @ts-ignore - WebCodecs API可能不在TS类型定义中
  return 'VideoDecoder' in window && 'VideoFrame' in window
}

/**
 * 初始化视频解码器
 */
export async function initVideo(config?: Partial<VideoConfig>): Promise<boolean> {
  try {
    // 合并配置
    state.config = {
      ...state.config,
      ...config
    }

    // 检查浏览器支持
    if (!isVideoDecoderSupported()) {
      log('浏览器不支持WebCodecs API，视频解码功能不可用', 'error')
      return false
    }

    log('视频解码器初始化成功', 'success')
    state.isInitialized = true
    return true
  } catch (error) {
    log(`初始化视频解码器失败: ${error}`, 'error')
    return false
  }
}

/**
 * 创建VideoDecoder实例
 */
function createVideoDecoder(): VideoDecoder | null {
  if (!isVideoDecoderSupported()) {
    log('浏览器不支持VideoDecoder', 'error')
    return null
  }

  try {
    // @ts-ignore
    const decoder = new VideoDecoder({
      output: handleDecodedFrame,
      error: (error: Error) => {
        log(`视频解码错误: ${error.message}`, 'error')
      }
    })

    // 配置解码器
    const codecString = state.config.codec === 'h264' 
      ? 'avc1.42E01E'  // H.264 Baseline Profile
      : 'vp09.00.10.08'  // VP9

    decoder.configure({
      codec: codecString,
      hardwareAcceleration: state.config.enableHardwareAcceleration ? 'prefer-hardware' : 'prefer-software'
    })

    log(`VideoDecoder已配置: ${codecString}`, 'info')
    return decoder
  } catch (error) {
    log(`创建VideoDecoder失败: ${error}`, 'error')
    return null
  }
}

/**
 * 处理解码后的视频帧
 */
function handleDecodedFrame(frame: any): void {
  try {
    frameCount++
    const now = Date.now()
    
    if (now - lastFrameTime > 1000) {
      const fps = frameCount
      log(`视频帧率: ${fps} FPS`, 'debug')
      
      // 通知视频正在播放
      if (videoPlayingCallback) {
        videoPlayingCallback(true, fps)
      }
      
      frameCount = 0
      lastFrameTime = now
    }

    // 渲染到Canvas
    if (state.canvasContext && state.targetElement instanceof HTMLCanvasElement) {
      const canvas = state.targetElement
      canvas.width = frame.displayWidth || state.config.width || 640
      canvas.height = frame.displayHeight || state.config.height || 480
      
      state.canvasContext.drawImage(frame, 0, 0, canvas.width, canvas.height)
    }

    // 关闭帧以释放资源
    frame.close()
  } catch (error) {
    log(`渲染视频帧失败: ${error}`, 'error')
  }
}

// =============================
// 视频解码
// =============================

/**
 * 处理二进制视频消息
 * @param data 视频数据（H.264编码的ArrayBuffer）
 */
export async function handleBinaryVideoMessage(data: ArrayBuffer): Promise<boolean> {
  try {
    if (!state.isInitialized) {
      log('视频解码器未初始化', 'warning')
      return false
    }

    log(`收到视频数据: ${data.byteLength} bytes`, 'debug')

    // 懒加载创建decoder
    if (!state.decoder) {
      state.decoder = createVideoDecoder()
      if (!state.decoder) {
        log('无法创建VideoDecoder', 'error')
        return false
      }
    }

    // 创建EncodedVideoChunk
    // @ts-ignore
    const chunk = new EncodedVideoChunk({
      type: 'key',  // 简化处理，假设所有帧都是关键帧
      timestamp: Date.now() * 1000,  // 微秒
      data: data
    })

    // 解码
    state.decoder.decode(chunk)
    
    return true
  } catch (error) {
    log(`处理视频消息失败: ${error}`, 'error')
    return false
  }
}

/**
 * 附加到DOM元素
 * @param elementId 目标元素ID（Canvas或Video）
 */
export function attachToElement(elementId: string): boolean {
  try {
    const element = document.getElementById(elementId)
    
    if (!element) {
      log(`未找到元素: ${elementId}`, 'error')
      return false
    }

    if (element instanceof HTMLCanvasElement) {
      state.targetElement = element
      state.canvasContext = element.getContext('2d')
      log(`视频解码器已附加到Canvas: ${elementId}`, 'success')
      return true
    } else if (element instanceof HTMLVideoElement) {
      state.targetElement = element
      log(`视频解码器已附加到Video: ${elementId}`, 'success')
      return true
    } else {
      log(`不支持的元素类型: ${element.tagName}`, 'error')
      return false
    }
  } catch (error) {
    log(`附加到元素失败: ${error}`, 'error')
    return false
  }
}

/**
 * 销毁视频解码器
 */
export function destroyVideo(): void {
  try {
    if (state.decoder) {
      state.decoder.close()
      state.decoder = null
    }

    state.frameQueue = []
    state.targetElement = null
    state.canvasContext = null
    state.isInitialized = false
    frameCount = 0
    lastFrameTime = 0
    
    // 通知视频停止播放
    if (videoPlayingCallback) {
      videoPlayingCallback(false, 0)
    }

    log('视频解码器已销毁', 'info')
  } catch (error) {
    log(`销毁视频解码器失败: ${error}`, 'error')
  }
}

// =============================
// 工具函数
// =============================

/**
 * 获取解码器状态
 */
export function getVideoDecoderState(): {
  isSupported: boolean
  isInitialized: boolean
  frameQueueLength: number
  codec: string
} {
  return {
    isSupported: isVideoDecoderSupported(),
    isInitialized: state.isInitialized,
    frameQueueLength: state.frameQueue.length,
    codec: state.config.codec || 'unknown'
  }
}

/**
 * 重置帧计数器
 */
export function resetFrameCounter(): void {
  frameCount = 0
  lastFrameTime = Date.now()
}

/**
 * 注册视频播放状态回调
 */
export function registerVideoPlayingCallback(callback: VideoPlayingCallback): void {
  videoPlayingCallback = callback
}

/**
 * 取消注册视频播放状态回调
 */
export function unregisterVideoPlayingCallback(): void {
  videoPlayingCallback = null
}
